// components/onboarding/wizard/steps.jsx
//
// Presentational building blocks + per-step bodies for the guided onboarding wizard.
// Each step body is a pure view over the `useOnboardingWizard` state object (`wiz`) - it renders
// inputs and wires them to the hook's form setters / actions. Footer navigation (Back / Skip /
// primary CTA) lives in OnboardingWizard.jsx, so these bodies stay focused on the fields.

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DOC_TILES } from '../../../hooks/useOnboardingWizard';

// ── shared primitives ────────────────────────────────────────────────────────
export const Spinner = ({ className = 'h-4 w-4' }) => (
  <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

const inputBase =
  'w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 focus:ring-4';

function inputCls(invalid) {
  return `${inputBase} ${invalid
    ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
    : 'border-gray-200 focus:border-blue-400 focus:ring-blue-100'}`;
}

export function Field({ label, hint, error, optional, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-2 text-sm font-medium text-gray-700">
        {label}
        {optional && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">Optional</span>}
      </span>
      {children}
      {(error || hint) && (
        <span className={`mt-1 block text-xs ${error ? 'text-red-600' : 'text-gray-400'}`}>{error || hint}</span>
      )}
    </label>
  );
}

export function Input({ value, onChange, invalid, upper, digits, maxLength, type = 'text', placeholder, inputMode }) {
  return (
    <input
      type={type}
      value={value}
      maxLength={maxLength}
      inputMode={inputMode}
      placeholder={placeholder}
      onChange={(e) => {
        let v = e.target.value;
        if (digits) v = v.replace(/\D/g, '');
        if (upper) v = v.toUpperCase();
        onChange(v);
      }}
      className={inputCls(invalid)}
    />
  );
}

// User-safe error block: friendly message + optional support reference id.
export function FieldError({ message, refId }) {
  if (!message) return null;
  return (
    <div className="mt-3 rounded-xl border border-red-100 bg-red-50/70 px-3.5 py-2.5">
      <p className="flex items-start gap-1.5 text-sm text-red-700">
        <svg className="mt-0.5 h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
        <span>{message}</span>
      </p>
      {refId && (
        <p className="mt-1 pl-6 text-[11px] text-red-400">
          Reference: <span className="font-mono">{refId}</span> - quote this to support if it keeps happening.
        </p>
      )}
    </div>
  );
}

// A friendly header for each step body.
function StepIntro({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-6">
      {eyebrow && <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600">{eyebrow}</p>}
      <h2 className="text-2xl font-bold text-gray-900 sm:text-[26px]">{title}</h2>
      {subtitle && <p className="mt-1.5 text-[15px] text-gray-500">{subtitle}</p>}
    </div>
  );
}

// ── step bodies ───────────────────────────────────────────────────────────────
export function ProfileStepBody({ wiz }) {
  const { form, set, sectionError, sectionErrorRef } = wiz;
  return (
    <div>
      <StepIntro eyebrow="Step 1 of 5" title="Where do we pick up your orders?" subtitle="Your primary pickup / communication address. You can add more pickup points later." />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Address line 1"><Input value={form.line1} onChange={set('line1')} placeholder="Building, street" /></Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Address line 2" optional><Input value={form.line2} onChange={set('line2')} placeholder="Area, landmark" /></Field>
        </div>
        <Field label="City"><Input value={form.city} onChange={set('city')} placeholder="Bengaluru" /></Field>
        <Field label="State"><Input value={form.state} onChange={set('state')} placeholder="Karnataka" /></Field>
        <Field label="Pincode"><Input value={form.pincode} onChange={set('pincode')} digits maxLength={6} inputMode="numeric" placeholder="560001" /></Field>
      </div>
      <FieldError message={sectionError.PROFILE} refId={sectionErrorRef?.PROFILE} />
    </div>
  );
}

export function BusinessStepBody({ wiz }) {
  const { form, set, sectionError, sectionErrorRef } = wiz;
  return (
    <div>
      <StepIntro eyebrow="Step 2 of 5" title="Your business identity" subtitle="Add at least one of PAN, GST or Aadhaar. PAN is recommended for the fastest verification." />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="PAN" hint="Format: ABCDE1234F"><Input value={form.pan} onChange={set('pan')} upper maxLength={10} placeholder="ABCDE1234F" /></Field>
        <Field label="GST" optional><Input value={form.gst} onChange={set('gst')} upper maxLength={15} placeholder="22ABCDE1234F1Z5" /></Field>
        <Field label="Aadhaar" optional><Input value={form.aadhaar} onChange={set('aadhaar')} digits maxLength={12} inputMode="numeric" placeholder="XXXX XXXX XXXX" /></Field>
      </div>
      <FieldError message={sectionError.BUSINESS} refId={sectionErrorRef?.BUSINESS} />
    </div>
  );
}

export function DocumentsStepBody({ wiz }) {
  const { uploadedDocs, uploading, onUpload, sectionError, sectionErrorRef } = wiz;
  return (
    <div>
      <StepIntro eyebrow="Step 3 of 5" title="Upload a KYC document" subtitle="Any one document gets you verified (PAN recommended). Aadhaar is optional — add front and/or back." />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {DOC_TILES.map(({ type, label, hint }) => {
          const done = uploadedDocs.includes(type.toUpperCase());
          const busy = uploading === type;
          return (
            <label
              key={type}
              className={`relative flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed p-4 text-center text-xs transition ${
                done ? 'border-green-300 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50/40'
              }`}
            >
              <span className="text-2xl" aria-hidden="true">{busy ? '' : done ? '✓' : '⬆'}</span>
              {busy && <Spinner className="h-6 w-6 text-blue-500" />}
              <span className="font-semibold">{label}</span>
              <span className="text-[10px] text-gray-400">{done ? 'Uploaded - replace' : hint || 'PDF / JPG / PNG'}</span>
              <input
                type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" disabled={busy}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(type, f); e.target.value = ''; }}
              />
            </label>
          );
        })}
      </div>
      <FieldError message={sectionError.DOCUMENTS} refId={sectionErrorRef?.DOCUMENTS} />
      <p className="mt-3 text-xs text-gray-400">Your documents are encrypted and used only for KYC verification.</p>
    </div>
  );
}

export function BankStepBody({ wiz }) {
  const { form, set, walletType, setWalletType, sectionError, sectionErrorRef } = wiz;
  const prefilled = !!(form.accNo || form.ifsc || form.accHolder || form.bankName || form.branch);
  const [adding, setAdding] = useState(prefilled);

  const clearBank = () => {
    ['accNo', 'accHolder', 'ifsc', 'bankName', 'branch'].forEach((k) => set(k)(''));
    setAdding(false);
  };

  return (
    <div>
      <StepIntro eyebrow="Step 4 of 5 · Optional" title="Bank & wallet" subtitle="Needed only before payouts and COD remittance - add it now or anytime later." />

      {!adding ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 p-6 text-center sm:p-8">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-2xl" aria-hidden="true">🏦</div>
          <p className="text-base font-semibold text-gray-900">Add bank &amp; wallet details now?</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">This is optional for getting verified - it’s only required before your first payout or COD remittance.</p>
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-5 py-2.5 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-50"
          >
            + Add bank details
          </button>
          <p className="mt-3 text-xs text-gray-400">Or just continue - you can add this later from your profile.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Account number"><Input value={form.accNo} onChange={set('accNo')} digits placeholder="00123456789" /></Field>
            <Field label="Account holder name"><Input value={form.accHolder} onChange={set('accHolder')} placeholder="Acme Retail Pvt Ltd" /></Field>
            <Field label="IFSC" hint="Format: HDFC0001234"><Input value={form.ifsc} onChange={set('ifsc')} upper maxLength={11} placeholder="HDFC0001234" /></Field>
            <Field label="Bank name"><Input value={form.bankName} onChange={set('bankName')} placeholder="HDFC Bank" /></Field>
            <Field label="Branch" optional><Input value={form.branch} onChange={set('branch')} placeholder="MG Road" /></Field>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-gray-700">Wallet preference</span>
              <select
                value={walletType}
                onChange={(e) => setWalletType(e.target.value)}
                className={`${inputBase} border-gray-200 focus:border-blue-400 focus:ring-blue-100`}
              >
                <option value="PREPAID">Prepaid</option>
                <option value="POSTPAID">Postpaid</option>
              </select>
            </label>
          </div>
          <FieldError message={sectionError.BANK} refId={sectionErrorRef?.BANK} />
          <button type="button" onClick={clearBank} className="mt-3 text-xs font-medium text-gray-400 hover:text-gray-600">I’ll add this later</button>
        </>
      )}
    </div>
  );
}

function SummaryRow({ label, value, done, optional, index = 0 }) {
  const chip = done
    ? 'bg-green-100 text-green-700'
    : optional
      ? 'bg-slate-100 text-slate-500'
      : 'bg-amber-100 text-amber-700';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 * index, duration: 0.3, ease: 'easeOut' }}
      className="flex items-start justify-between gap-3 border-b border-gray-100 py-3 last:border-0"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="truncate text-xs text-gray-500">{value || (optional ? 'Add anytime - not required to submit' : 'Not added yet')}</p>
      </div>
      <span className={`mt-0.5 inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2.5 text-[11px] font-semibold ${chip}`}>
        {done ? '✓ Done' : optional ? 'Optional' : 'Pending'}
      </span>
    </motion.div>
  );
}

export function ReviewStepBody({ wiz, onJumpTo }) {
  const { form, phaseComplete, requiredDone, submitError, sectionErrorRef, uploadedDocs } = wiz;
  return (
    <div>
      <StepIntro eyebrow="Step 5 of 5" title="Review & submit" subtitle={requiredDone ? 'Everything required is in place. Submit and our team will verify your account.' : 'Finish the pending steps below, then submit for verification.'} />
      <div className="rounded-2xl border border-gray-200 bg-gray-50/60 px-4">
        <SummaryRow index={0} label="Account & contact" value={form.email ? `${form.name || 'You'} · ${form.email}` : ''} done={!!phaseComplete.ACCOUNT} />
        <SummaryRow index={1} label="Profile & address" value={[form.line1, form.city, form.state, form.pincode].filter(Boolean).join(', ')} done={!!phaseComplete.PROFILE} />
        <SummaryRow index={2} label="Business & PAN" value={[form.pan, form.gst, form.aadhaar].filter(Boolean).join(' · ')} done={!!phaseComplete.BUSINESS} />
        <SummaryRow index={3} label="Documents" value={uploadedDocs.length ? uploadedDocs.join(', ') : ''} done={!!phaseComplete.DOCUMENTS} />
        <SummaryRow index={4} label="Bank & wallet" value={form.accNo ? `A/C ••••${form.accNo.slice(-4)} · ${form.ifsc}` : ''} done={!!phaseComplete.BANK} optional />
      </div>

      {requiredDone ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="mt-4 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
        >
          <span aria-hidden="true">✓</span>
          <p>All required details are in. <span className="font-semibold">Bank &amp; wallet is optional</span> - add it later before your first payout. You’re good to submit.</p>
        </motion.div>
      ) : (
        <div className="mt-4 flex flex-wrap gap-2">
          {['PROFILE', 'BUSINESS', 'DOCUMENTS'].filter((k) => !phaseComplete[k]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => onJumpTo?.(k)}
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
            >
              Finish {k === 'PROFILE' ? 'Profile & address' : k === 'BUSINESS' ? 'Business & PAN' : 'Documents'} →
            </button>
          ))}
        </div>
      )}

      <FieldError message={submitError} refId={sectionErrorRef?.REVIEW} />
    </div>
  );
}
